import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const ExperienciasContainer = styled.div`
  width: 100%;
  min-height: 100vh;
  background: var(--color-dark);
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Title = styled(motion.h1)`
  color: var(--color-white);
  font-size: 3rem;
  margin-bottom: 3rem;
  text-align: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 3px;
    background-color: var(--color-primary);
    border-radius: 3px;
  }
`;

const ExperienceGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  width: 100%;
  max-width: 1200px;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ExperienceCard = styled(motion.div)`
  background: rgba(26, 26, 26, 0.7);
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 144, 82, 0.2);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
`;

const ExperienceHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid rgba(255, 144, 82, 0.2);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 5px;
    height: 100%;
    background: var(--color-primary);
  }
`;

const ExperienceTitle = styled.h3`
  color: var(--color-white);
  font-size: 1.764rem;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`;

const ExperienceCompany = styled.h4`
  color: var(--color-primary);
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
`;

const ExperiencePeriod = styled.div`
  color: var(--color-white);
  opacity: 0.7;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ExperienceContent = styled.div`
  padding: 1.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const ExperienceDescription = styled.p`
  color: var(--color-white);
  opacity: 0.7;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ExperienceAchievements = styled.div`
  margin-top: auto;
`;

const AchievementTitle = styled.h5`
  color: var(--color-white);
  font-size: 1rem;
  margin-bottom: 0.8rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &::before {
    content: '🏆';
  }
`;

const AchievementList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const AchievementItem = styled.li`
  color: var(--color-white);
  opacity: 0.7;
  margin-bottom: 0.5rem;
  padding-left: 1.5rem;
  position: relative;
  line-height: 1.5;
  
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: var(--color-primary);
  }
`;

const ExperienceTech = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const TechTag = styled.span`
  background: rgba(255, 144, 82, 0.2);
  color: var(--color-white);
  padding: 0.3rem 0.8rem;
  border-radius: 50px;
  font-size: 0.8rem;
  border: 1px solid rgba(255, 144, 82, 0.3);
`;

const LevelBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: var(--color-primary);
  color: var(--color-white);
  padding: 0.254rem 0.635rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: bold;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.3);
`;

const BackButton = styled(motion.button)`
  background: rgba(26, 26, 26, 0.7);
  color: var(--color-white);
  font-size: 1rem;
  padding: 0.8rem 1.5rem;
  border-radius: 50px;
  border: 2px solid var(--color-primary);
  margin-top: 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 15px rgba(255, 144, 82, 0.2);
  backdrop-filter: blur(5px);
  
  &:hover {
    background: rgba(255, 144, 82, 0.2);
  }
`;

const Experiencias: React.FC<{ onBack: () => void }> = ({ onBack }) => {
const experiences = [
  {
    title: "Software Engineer",
    company: "ExitLag",
    period: "Sep 2024 - Present",
    level: "Level 5",
    description: "Leading the development of a new backend structure and building a microservices architecture for connection optimization solutions.",
    achievements: [
      "Led the development of a scalable microservices architecture enhancing system reliability.",
      "Improved backend performance significantly, enhancing user experience for gaming connection services.",
      "Implemented innovative solutions increasing overall team productivity."
    ],
    technologies: ["Node.js", "Microservices", "Redis", "React", "C++", "Unreal Engine"]
  },
  {
    title: "Software Engineer",
    company: "Jostens",
    period: "Apr 2023 - Sep 2024",
    level: "Level 4",
    description: "Migrated and maintained an ecommerce platform built in Shopify to a new architecture using React, Node.js with Nest.js, and microservices for both ecommerce platform and admin panel.",
    achievements: [
      "Successfully migrated complex ecommerce architecture to modern tech stack.",
      "Improved platform maintainability and scalability.",
      "Enhanced system performance and user experience significantly."
    ],
    technologies: ["React", "Node.js", "Nest.js", "Shopify", "Microservices"]
  },
  {
    title: "Software Engineer",
    company: "Softensity",
    period: "Oct 2022 - Apr 2023",
    level: "Level 4",
    description: "Development of Next.js applications and legacy system migrations, providing technical leadership in architectural decisions and project estimations.",
    achievements: [
      "Developed and maintained scalable Next.js applications.",
      "Effectively managed legacy system migrations.",
      "Provided key architectural insights improving system performance."
    ],
    technologies: ["Next.js", "React", "Node.js", "Legacy Migrations"]
  },
  {
    title: "Software Architect Manager",
    company: "CI&T",
    period: "Dec 2021 - Sep 2024",
    level: "Level 4",
    description: "Technical leadership overseeing multiple development squads, architectural decision-making, and project estimations.",
    achievements: [
      "Led the architectural design and successful migration to micro-frontends architecture.",
      "Mentored and developed technical teams, enhancing their professional growth.",
      "Improved deployment efficiency significantly through CI/CD practices."
    ],
    technologies: ["React", "JavaScript", "Node.js", "AWS", "Kubernetes", "Micro-frontends"]
  },
  {
    title: "Tech Lead",
    company: "Dextra",
    period: "Apr 2021 - Dec 2021",
    level: "Level 3",
    description: "Leadership in technical decision-making, mentoring teams, and migrating legacy systems to modern architectures.",
    achievements: [
      "Successfully completed migrations to micro-frontends, improving scalability.",
      "Provided critical mentorship and training for developers.",
      "Enhanced project estimation accuracy and delivery timelines."
    ],
    technologies: ["Micro-frontends", "React", "Node.js", "AWS"]
  },
  {
    title: "Senior Software Engineer",
    company: "PagSeguro PagBank",
    period: "Aug 2020 - Apr 2021",
    level: "Level 2",
    description: "Developed instant payment solutions and recurring payment portals.",
    achievements: [
      "Contributed significantly to Brazil's PIX instant payment system using Kotlin and Spring.",
      "Led the creation of a high-performing recurring payment platform using Next.js and React.js.",
      "Optimized backend processes for increased transaction security and efficiency."
    ],
    technologies: ["Kotlin", "Spring", "Next.js", "React", "AWS"]
  },
  {
    title: "Frontend Engineer",
    company: "Dextra Digital",
    period: "Mar 2018 - Aug 2020",
    level: "Level 2",
    description: "Worked on projects for major clients, enhancing web applications and backend systems.",
    achievements: [
      "Developed and maintained backend systems for Globo.com's Olympics project and Nextel's platforms.",
      "Implemented robust solutions using AWS services and improved system scalability.",
      "Achieved notable performance enhancements in customer-facing applications."
    ],
    technologies: ["Go", "Node.js", "React", "AngularJS", "AWS", "MySQL", "MongoDB"]
  }
];


  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        type: "spring", 
        stiffness: 100, 
        damping: 12
      }
    }
  };

  return (
    <ExperienciasContainer>
      <Title
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        Skills
      </Title>
      
      <ExperienceGrid
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {experiences.map((experience, index) => (
          <ExperienceCard
            key={index}
            variants={itemVariants}
          >
            <LevelBadge>{experience.level}</LevelBadge>
            <ExperienceHeader>
              <ExperienceTitle>{experience.title}</ExperienceTitle>
              <ExperienceCompany>{experience.company}</ExperienceCompany>
              <ExperiencePeriod>
                <span role="img" aria-label="calendar">📅</span>
                {experience.period}
              </ExperiencePeriod>
            </ExperienceHeader>
            <ExperienceContent>
              <ExperienceDescription>{experience.description}</ExperienceDescription>
              <ExperienceAchievements>
                <AchievementTitle>Achievements</AchievementTitle>
                <AchievementList>
                  {experience.achievements.map((achievement, i) => (
                    <AchievementItem key={i}>{achievement}</AchievementItem>
                  ))}
                </AchievementList>
              </ExperienceAchievements>
              <ExperienceTech>
                {experience.technologies.map((tech, i) => (
                  <TechTag key={i}>{tech}</TechTag>
                ))}
              </ExperienceTech>
            </ExperienceContent>
          </ExperienceCard>
        ))}
      </ExperienceGrid>
      
      <BackButton 
        onClick={onBack}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Back to Menu
      </BackButton>
    </ExperienciasContainer>
  );
};

export default Experiencias;
